import React, { useEffect } from 'react';
import { t } from 'utils/getTranslations.ts';
import { isNumeric } from 'utils/number.ts';
import { getSelectorKey } from '../../getSelectorKey.ts';
import { MainFilterForm, NumberFilterForm, QuickFilterForm } from '../../ListTableTypes.tsx';
import { ClearableInput } from './ClearableInput';
import { FilterLabel } from './FilterLabel';

/**
 * One number the user types, for a filter with a single threshold rather than the two ends `Interval`
 * offers. It is a plain text field guarded by `isNumeric`, not an `input[type=number]`, so it looks and
 * behaves like the interval fields it sits beside in the same drawer.
 *
 * An emptied field reports `null`, which is what removes the clause — distinct from a typed `0`.
 */
export const NumberField: React.FC<{
    filter: NumberFilterForm;
    quickFilters: QuickFilterForm;
    mainFilters: MainFilterForm;
    onChange: (value: number | null, groupSelector: string) => void;
}> = React.memo(({ filter, onChange, quickFilters, mainFilters }) => {
    const isHidden =
        filter.getShouldHide !== undefined
            ? filter.getShouldHide({
                  mainFilters,
                  quickFilters,
                  groupSelector: getSelectorKey(filter),
              })
            : false;

    useEffect(() => {
        // Only when there is something to drop: `onChange` re-renders the whole drawer, so clearing an
        // already-empty filter on every render of a hidden one would loop.
        if (isHidden && !filter?.preserveValueWhenHidden && filter.value != null) {
            onChange(null, getSelectorKey(filter));
        }
    }, [filter, isHidden, onChange]);

    if (isHidden) {
        return null;
    }

    return (
        <div className="flex grow flex-col gap-1" key={`filter-${getSelectorKey(filter)}`}>
            {filter.label && (
                <FilterLabel label={filter.label} tooltip={filter.tooltip} tooltipValues={filter.tooltipValues} />
            )}
            <ClearableInput
                value={filter.value != null ? String(filter.value) : ''}
                placeholder={t(filter.placeholder ?? 'forms.enter-number')}
                ariaLabel={filter.label}
                onClear={() => onChange(null, getSelectorKey(filter))}
                onChange={(e) => {
                    const raw = e.target.value;
                    // A keystroke that is not a number, or lands outside the filter's bounds, is ignored
                    // rather than committed. That includes a lone "-", which the interval fields let
                    // through as the start of a negative bound. Only an emptied field clears the clause,
                    // which is how a bounded field still lets go of its value.
                    if (raw === '') {
                        onChange(null, getSelectorKey(filter));
                        return;
                    }
                    if (!isNumeric(raw)) {
                        return;
                    }
                    // `isNumeric` accepts "Infinity", which no comparison downstream can use.
                    const parsed = Number(raw);
                    if (!Number.isFinite(parsed)) {
                        return;
                    }
                    if (
                        (filter.min !== undefined && parsed < filter.min) ||
                        (filter.max !== undefined && parsed > filter.max)
                    ) {
                        return;
                    }
                    onChange(parsed, getSelectorKey(filter));
                }}
            />
        </div>
    );
});
